<template>
  <div class="chart">
    <div class="chart-data-container">
      <div>
        <component :is="type"
                   :width="initialSize.width"
                   :height="initialSize.height"
                   :plugins="plugins"
                   ref="component">
        </component>
      </div>
      <btn size="xs" class="pull-right" type="primary"
           @click="doExport" :disabled="!showExport">Exporter</btn>
    </div>
    <modal v-model="exportModal"
           size="lg"
           title="Exporter un graphique"
           :append-to-body="nested"
           :footer="false">
      <div class="hd-container">
        <component :is="type" ref="hd" :style="style"
                              :width="canvasSize.width"
                              :height="canvasSize.height"
                              :key="`canvas-${ratio}`"
                              :plugins="hdPlugins">
        </component>
      </div>
      <div v-if="!sizeError" v-show="img !== ''" class="result-container">
        <div class="row">
          <div class="col-xs-8">
            <img class="img-container img-responsive" :src="img" ref="target">
          </div>
          <div class="col-xs-4">
            <p>
              Pour copier l'image, faites
              <strong>clic droit > copier l'image</strong>.
            </p>
            <p>
              Pour enregistrer l'image, faites
              <strong>clic droit > enregistrer l'image sous...</strong>
            </p>
          </div>
        </div>
      </div>
      <hr>
      <div v-if="exportModal">
        <div class="row">
          <div class="col-xs-6 form-group">
            <label>Largeur de l'image (cm)</label>
            <cleave v-model="size"
                    :options="{ numeral: true }"
                    class="form-control"
                    autocomplete="off"
                    name="numeric">
            </cleave>
            <alert type="danger"
                   v-if="sizeError">
              La taille saisie est invalide, elle doit être supérieure à
              {{ minExportSize }}cm.
            </alert>
          </div>
          <div class="col-xs-6 form-group">
            <label>Ratio largeur / hauteur</label>
            <cleave v-model="ratio"
                    :options="{ numeral: true }"
                    class="form-control"
                    autocomplete="off"
                    name="numeric">
            </cleave>
          </div>
        </div>
        <div v-for="(l, k) in optionLists"
             :key="`option-list-${k}`"
             class="row">
         <div class="col-xs-12" v-if="l.length > 0">
            <hr />
          </div>
          <div v-for="(c, k) in l"
               :key="`export-config-${c.key}`">
            <div class="col-xs-6 form-group">
              <div v-if="c.type === 'checkbox'" class="checkbox">
                <c-checkbox v-model="exportValues[c.key]" block size="sm"
                            @input="triggerCheckbox(c)"
                       type="reaInfADI">
                  {{ c.label }}
                </c-checkbox>
              </div>
              <div v-else-if="c.type === 'text'">
                <label>{{ c.label }}</label>
                <input ref="input"
                       class="form-control"
                       v-model="exportValues[c.key]" />
              </div>
              <div v-else-if="c.type === 'list'">
                <label>{{ c.label }}</label>
                <dropdown tag="div">
                  <btn role="button" class="dropdown-toggle" block type="primary">
                    <strong>{{ c.values[exportValues[c.key]].label }}</strong>
                    &nbsp;&nbsp;<span class="caret"></span>
                  </btn>
                  <template slot="dropdown">
                    <li v-for="(elt, x) in c.values"
                       :key="`export-config-${c.key}-${x}`">
                      <a role="button"
                         @click="exportValues[c.key] = x">
                        {{ elt.label }}
                      </a>
                    </li>
                  </template>
                </dropdown>
              </div>
              <div v-else>
                <label>{{ c.label }}</label>
                <cleave v-model="exportValues[c.key]"
                        :options="c.options"
                        class="form-control"
                        autocomplete="off">
                </cleave>
              </div>
            </div>
            <div v-if="!((k + 1) % 2)" class="clearfix"></div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>
<script>
import pie from '@/sui/components/charts/pie'
import doughnut from '@/sui/components/charts/doughnut'
import bar from '@/sui/components/charts/bar'
import hbar from '@/sui/components/charts/hbar'
import line from '@/sui/components/charts/line'
import radar from '@/sui/components/charts/radar'
import polar from '@/sui/components/charts/polar'
import boxplot from '@/sui/components/charts/boxplot'
import violin from '@/sui/components/charts/violin'
import cleave from 'vue-cleave-component'
import merge from '@/sui/tools/merge'
import checkbox from '@/sui/components/checkbox'

export default {
  name: 'chart',

  props: {
    type: {
      type: String,
      required: true
    },

    config: {
      type: Object,
      required: true
    },

    nested: {
      type: Boolean,
      default: false
    },

    plugins: {
      type: Array,
      default: () => []
    },

    minExportSize: {
      type: Number,
      default: 5
    },

    exportConfig: {
      type: Array,
      default: () => []
    },

    initialWidth: {
      type: Number,
      default: 400
    },

    initialRatio: {
      type: Number,
      default: 1
    }
  },

  data: function () {
    const result = {
      exportValues: {},
      showExport: false,
      exportModal: false,
      size: '20',
      ratio: `${this.initialRatio}`,
      img: '',
      generating: false
    }
    return result
  },
  computed: {
    style: function () {
      return {
        width: `${this.size}cm`
      }
    },

    initialSize: function () {
      return {
        width: this.initialWidth,
        height: this.initialWidth / this.initialRatio
      }
    },

    canvasSize: function () {
      const base = {
        width: this.initialWidth,
        height: this.initialWidth
      }

      if (this.ratio === '' || this.ratio === '1') {
        return base
      }

      const ratio = parseFloat(this.ratio)
      if (ratio > 1) {
        return {
          width: base.width * ratio,
          height: base.height
        }
      }

      return {
        width: base.width,
        height: base.height / ratio
      }
    },

    sizeError: function () {
      if (this.size === '') {
        return true
      }
      return parseInt(this.size) < this.minExportSize
    },

    hdPlugins: function () {
      const result = [
        {
          beforeDraw: function (chart) {
            const width = chart.chart.width
            const height = chart.chart.height
            const ctx = chart.chart.ctx
            ctx.save()
            ctx.fillStyle = 'white'
            ctx.fillRect(0, 0, width, height)
            ctx.restore()
          }
        },
        ...this.plugins
      ]

      return result
    },

    hdOptions: function () {
      const vm = this
      let result = {
        animation: {
          duration: 0,
          onComplete: function (d) {
            vm.img = d.chart.canvas.toDataURL('image/jpeg', 1.0)
          }
        }
      }

      result = merge(result, this.config.options, true)

      this.exportConfig.forEach(
        e => {
          if (e.toConfig == null) {
            return
          }

          result = merge(
            result,
            e.toConfig(this.exportValues[e.key], this.config.options),
            true
          )
        }
      )

      return result
    },

    options: function () {
      const vm = this
      let result = {
        animation: {
          onComplete: function () {
            vm.setShowExport()
          }
        }
      }
      result = merge(result, this.config.options, true)
      this.exportConfig.forEach(
        e => {
          if (e.toConfig == null) {
            return
          }

          result = merge(
            result,
            e.toConfig(this.exportValues[e.key], this.config.options),
            true
          )
        }
      )
      return result
    },

    chartData: function () {
      return this.config.data
    },

    optionLists: function () {
      const cb = []
      const other = []
      this.exportConfig.forEach(
        o => {
          if (o.type === 'checkbox') {
            cb.push(o)
          } else {
            other.push(o)
          }
        }
      )

      return [other, cb]
    }
  },

  watch: {
    chartData: function () {
      this.$nextTick(
        () => {
          this.update()
        }
      )
    },

    exportConfig: {
      handler (val) {
        this.initializeExportValues(val)
      },
      deep: true
    },

    exportValues: {
      handler () {
        if (this.exportModal) {
          this.generateHD()
        }
      },
      deep: true
    },

    canvasSize: {
      handler () {
        if (this.exportModal) {
          this.generateHD(true)
        }
      },
      deep: true
    }
  },

  methods: {
    initializeExportValues: function (val) {
      this.exportValues = {}
      val.forEach(
        e => {
          this.$set(this.exportValues, e.key, e.default)
        }
      )
    },

    doExport: function () {
      this.exportModal = true
      this.initializeExportValues(this.exportConfig)
      this.generateHD()
    },

    generateHD: function (remove) {
      if (this.generating) {
        return
      }
      this.generating = true
      this.$nextTick(
        () => {
          if (remove) {
            this.$refs.hd.$forceUpdate()
          }
          this.$refs.hd.renderChart(this.chartData, this.hdOptions)

          let changed = false
          const rc = this.$refs.component._data._chart
          const tc = this.$refs.hd._data._chart
          for (let j = 0; j !== rc.config.data.datasets.length; ++j) {
            const ref = rc.getDatasetMeta(j)
            const target = tc.getDatasetMeta(j)
            for (let i = 0; i !== ref.data.length; ++i) {
              const rh = ref.data[i].hidden
              if (rh !== target.data[i].hidden) {
                target.data[i].hidden = rh
                changed = true
              }
            }

            if (ref.hidden !== target.hidden) {
              target.hidden = ref.hidden
              changed = true
            }
          }
          if (changed) {
            this.$refs.hd._data._chart.update()
          }
          this.generating = false
        }
      )
    },

    triggerCheckbox: function (c) {
      if (c.onChange != null) {
        c.onChange(this.exportValues[c.key])
      }
    },

    update: function () {
      this.showExport = false
      this.$refs.component.renderChart(this.chartData, this.options)
    },

    setShowExport: function () {
      this.initializeExportValues(this.exportConfig)

      this.showExport = true
    }
  },

  mounted: function () {
    this.initializeExportValues(this.exportConfig)
    this.update()
  },

  components: {
    'bar-chart': bar,
    'hbar-chart': hbar,
    'pie-chart': pie,
    'doughnut-chart': doughnut,
    'line-chart': line,
    'radar-chart': radar,
    'polar-chart': polar,
    'cleave': cleave,
    'boxplot-chart': boxplot,
    'violin-chart': violin,
    'c-checkbox': checkbox
  }
}
</script>
<style scoped lang="less">
.chart {
  padding: 0.3rem;
}

.hd-container {
  overflow: hidden;
  width: 1px;
  height: 1px;
}

.result-container {
  img {
    margin: auto;
  }
}

.chart-data-container {
  border: 1px solid #0a77a7;
  padding: 1rem;
  border-radius: 3px;
  background-color: white;
}

.img-container {
  border: 1px solid #0a77a7;
  border-radius: 3px;
}

.dropdown {
  width: 100%;
  .btn {
    text-align: left;
  }
}
</style>
